import React from 'react';
import styled from 'styled-components';
import colors, { TColorKeys } from '../../styles/colors';
import { vars } from '../../theme';
import { MustIncludeOneOfTestIdProps } from '../common/internal/test-id.types';
import HBSwitch, { HBSwitchDisabledProps, HBSwitchBaseProps } from './HBSwitch';
import HBText from '../HBText/HBText';
import HBClickable from '../HBClickable/HBClickable';

export type THBCustomSwitchOption = {
    value: string;
    label: string;
};

interface HBCustomSwitchBaseProps
    extends Omit<HBSwitchBaseProps, 'onChange' | 'checked' | 'color'> {
    /** Exactly two options: the first maps to unchecked and the second to checked. */
    options: [THBCustomSwitchOption, THBCustomSwitchOption];
    onChange: (option: string) => void;
    value: string;
    color?: TColorKeys;
}

export type HBCustomSwitchProps = HBCustomSwitchBaseProps &
    HBSwitchDisabledProps;

function HBCustomSwitch(
    props: HBCustomSwitchBaseProps & MustIncludeOneOfTestIdProps
): JSX.Element;
function HBCustomSwitch(
    props: HBCustomSwitchProps & MustIncludeOneOfTestIdProps
): JSX.Element;
function HBCustomSwitch({
    options,
    value,
    onChange,
    color,
    ...props
}: HBCustomSwitchProps & MustIncludeOneOfTestIdProps) {
    const { testId, testIdName } = props;
    const [option1, option2] = options;

    const _onChange = (checked: boolean) => {
        const newValue = checked ? option2.value : option1.value;
        onChange(newValue);
    };

    const checked = value === option2.value;

    const commonLabelProps = {
        onClick: onChange,
        testIdName,
        testId
    };

    return (
        <Container>
            <Label
                option={option1}
                isSelected={!checked}
                {...commonLabelProps}
            />
            <StyledHBSwitch
                {...props}
                onChange={_onChange}
                checked={checked}
                color={color}
                hideCheckIcon
            />
            <Label
                option={option2}
                isSelected={checked}
                {...commonLabelProps}
            />
        </Container>
    );
}

export default HBCustomSwitch;

interface SwitchLabelProps {
    option: THBCustomSwitchOption;
    isSelected: boolean;
    onClick: (value: string) => void;
}

const Label = ({
    isSelected,
    onClick,
    option,
    testId,
    testIdName
}: SwitchLabelProps & MustIncludeOneOfTestIdProps) => {
    return (
        <HBClickable
            onClick={() => onClick(option.value)}
            testId={testId}
            testIdName={testIdName}
        >
            <HBText
                color={isSelected ? 'text.primary' : 'text.secondary'}
                type="body16"
            >
                {option.label}
            </HBText>
        </HBClickable>
    );
};

// -------------------------------- Styles -------------------------------- //

type StyledProps = { color?: TColorKeys };

const DEFAULT_BG_COLOR = vars.colors.text.primary;

function getBackgroundColor({ color }: StyledProps) {
    if (color) {
        // Intentional TColorKeys override API; see .claude/rules/styling.md
        return `background-color: ${colors[color]};`;
    }
    return `background-color: ${DEFAULT_BG_COLOR};`;
}

const Container = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const StyledHBSwitch = styled(HBSwitch)<StyledProps>`
    margin: 0;

    // Required in order to override the HBSwitch styles
    &&&&,
    // Required in order to override angular button:focus styles
    &&&&:focus {
        ${getBackgroundColor}
    }
`;
